import { useState } from 'react';
import {
  App,
  Button,
  Descriptions,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { QuestionCircleOutlined, ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import type { RequestSettings } from '@core/entities/RequestSettings';
import {
  REQUEST_SETTINGS_FIELDS,
  UNSUPPORTED_SETTINGS_FIELDS,
  type FieldDescriptor,
  type FieldKey,
} from '@presentation/constants/requestSettingsFields';
import { useRequestSettingsDefaults } from '@presentation/hooks/useRequestSettings';
import { useWorkspace } from '@presentation/hooks/useWorkspaces';

interface SettingsPanelProps {
  workspaceId: string;
  settings: RequestSettings;
  onChange: (settings: RequestSettings) => void;
}

export const SettingsPanel = ({ workspaceId, settings, onChange }: SettingsPanelProps) => {
  const { message } = App.useApp();
  const { data: defaults, isLoading } = useRequestSettingsDefaults();
  const { data: workspace } = useWorkspace(workspaceId);
  const [search, setSearch] = useState('');

  const workspaceSettings = workspace?.settings ?? {};
  const effectiveDefault: RequestSettings = defaults
    ? { ...defaults.appDefaults, ...workspaceSettings }
    : {};

  const scopeFor = (key: FieldKey): { label: string; color: string } => {
    if (settings[key] !== undefined) return { label: 'Request', color: 'blue' };
    if (workspaceSettings[key] !== undefined)
      return { label: 'Workspace default', color: 'purple' };
    return { label: 'Application default', color: 'default' };
  };

  const setField = (key: FieldKey, value: unknown) => {
    onChange({ ...settings, [key]: value });
  };

  const resetField = (key: FieldKey) => {
    const next = { ...settings };
    delete next[key];
    onChange(next);
  };

  const resetAll = () => {
    onChange({});
    void message.success('Request settings reset to inherited defaults');
  };

  const query = search.trim().toLowerCase();
  const visibleFields = REQUEST_SETTINGS_FIELDS.filter(
    (field) =>
      !query || field.label.toLowerCase().includes(query) || field.keywords.includes(query),
  );
  const visibleUnsupported = UNSUPPORTED_SETTINGS_FIELDS.filter(
    (field) =>
      defaults?.unsupported[field.key] &&
      (!query || field.label.toLowerCase().includes(query) || field.keywords.includes(query)),
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="small" />
      </div>
    );
  }

  const renderControl = (field: FieldDescriptor) => {
    const value = settings[field.key];
    const placeholder = effectiveDefault[field.key];

    if (field.control.type === 'switch') {
      return (
        <Switch
          checked={(value as boolean | undefined) ?? (placeholder as boolean | undefined) ?? false}
          onChange={(checked) => setField(field.key, checked)}
        />
      );
    }
    if (field.control.type === 'select') {
      return (
        <Select
          size="small"
          className="w-40"
          value={(value as string | undefined) ?? (placeholder as string | undefined)}
          options={field.control.options}
          onChange={(next) => setField(field.key, next)}
        />
      );
    }
    if (field.control.type === 'number') {
      return (
        <InputNumber
          size="small"
          min={field.control.min}
          max={field.control.max}
          value={(value as number | undefined) ?? (placeholder as number | undefined)}
          onChange={(next) => setField(field.key, next ?? undefined)}
        />
      );
    }
    return (
      <Input
        size="small"
        className="!w-64"
        placeholder={field.control.placeholder}
        value={(value as string | null | undefined) ?? ''}
        onChange={(event) => setField(field.key, event.target.value || null)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Input.Search
          placeholder="Search settings… (e.g. SSL, redirect, cookie, TLS)"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          allowClear
          size="small"
          className="max-w-xs"
        />
        <Popconfirm
          title="Reset all request-level settings?"
          description="This clears every override on this request — workspace and application defaults still apply."
          onConfirm={resetAll}
        >
          <Button size="small" icon={<ReloadOutlined />}>
            Reset All Request Settings
          </Button>
        </Popconfirm>
      </div>

      <Typography.Text type="secondary" className="text-xs">
        Each setting falls back through Request → Workspace default → Application default. Changing
        a value here creates a request-specific override; use the undo icon to remove it and inherit
        again.
      </Typography.Text>

      <div className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
        {visibleFields.map((field) => {
          const scope = scopeFor(field.key);
          const isOverridden = settings[field.key] !== undefined;
          return (
            <div key={field.key} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">{field.label}</span>
                  <Tooltip title={field.description}>
                    <QuestionCircleOutlined className="text-xs text-gray-400" />
                  </Tooltip>
                </div>
                <Tag color={scope.color} className="mt-1">
                  {scope.label}
                </Tag>
              </div>
              {renderControl(field)}
              <Tooltip title="Reset to inherited value">
                <Button
                  size="small"
                  type="text"
                  icon={<UndoOutlined />}
                  disabled={!isOverridden}
                  aria-label={`Reset ${field.label}`}
                  onClick={() => resetField(field.key)}
                />
              </Tooltip>
            </div>
          );
        })}
      </div>

      {visibleUnsupported.length > 0 && (
        <div className="mt-2">
          <Typography.Text type="secondary" className="text-xs font-medium">
            Not supported by this server's request engine
          </Typography.Text>
          <Descriptions size="small" column={1} bordered className="mt-1">
            {visibleUnsupported.map((field) => (
              <Descriptions.Item
                key={field.key}
                label={<span className="text-gray-400">{field.label}</span>}
              >
                <div className="flex items-center justify-between gap-2">
                  <Typography.Text type="secondary" className="text-xs">
                    {defaults?.unsupported[field.key]}
                  </Typography.Text>
                  <Switch disabled size="small" />
                </div>
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      )}

      {visibleFields.length === 0 && visibleUnsupported.length === 0 && (
        <Typography.Text type="secondary" className="text-sm">
          No settings match "{search}".
        </Typography.Text>
      )}
    </div>
  );
};
